import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function DeletedJobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [purging, setPurging] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/jobs/deleted');
      setJobs(data);
    } catch { setError('Failed to load deleted jobs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const purge = async (job) => {
    if (!window.confirm(`Permanently delete "${job.job_number} — ${job.title}"?\n\nThis cannot be undone. All assemblies, operations, and time logs will be erased.`)) return;
    setPurging(job.id);
    try {
      await api.delete(`/jobs/${job.id}/purge`);
      load();
    } catch { setError('Failed to permanently delete job'); }
    finally { setPurging(null); }
  };

  const restore = async (job) => {
    if (!window.confirm(`Restore job "${job.job_number} — ${job.title}"?`)) return;
    setRestoring(job.id);
    try {
      await api.post(`/jobs/${job.id}/restore`);
      load();
    } catch { setError('Failed to restore job'); }
    finally { setRestoring(null); }
  };

  if (loading) return <div className="loading-screen">Loading…</div>;

  return (
    <div className="page">
      <div className="topbar">
        <button className="btn btn-ghost" style={{ padding:'6px 12px', fontSize:13 }} onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <h1>Deleted Jobs</h1>
        <div />
      </div>

      <div className="content">
        {error && <div className="error-msg" style={{ marginBottom:12 }}>{error}</div>}

        <div className="card">
          <div className="section-title" style={{ marginBottom: jobs.length ? 10 : 0 }}>
            {jobs.length} deleted job{jobs.length !== 1 ? 's' : ''}
          </div>

          {jobs.length === 0 && (
            <div style={{ color:'var(--gray3)', fontSize:14, padding:'12px 0' }}>
              No deleted jobs.
            </div>
          )}

          {jobs.map(j => (
            <div key={j.id} style={{
              display:'flex', alignItems:'center', gap:12,
              padding:'12px 0', borderBottom:'1px solid var(--gray5)',
            }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                  <span style={{ fontSize:13, color:'var(--gray3)', minWidth:72 }}>{j.job_number}</span>
                  <span style={{ fontWeight:600 }}>{j.title}</span>
                  <span className={`badge badge-${j.status}`}>{j.status.replace('_',' ')}</span>
                </div>
                <div style={{ fontSize:12, color:'var(--gray4)' }}>
                  {j.assembly_count} {parseInt(j.assembly_count) === 1 ? 'assembly' : 'assemblies'}
                  {j.due_date && ` · Due ${new Date(j.due_date).toLocaleDateString()}`}
                  {' · '}Deleted {new Date(j.deleted_at).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                </div>
              </div>
              <button
                className="btn btn-ghost"
                style={{ padding:'6px 14px', fontSize:13, flexShrink:0 }}
                disabled={restoring === j.id || purging === j.id}
                onClick={() => restore(j)}
              >
                {restoring === j.id ? 'Restoring…' : 'Restore'}
              </button>
              <button
                className="btn btn-danger"
                style={{ padding:'6px 14px', fontSize:13, flexShrink:0 }}
                disabled={restoring === j.id || purging === j.id}
                onClick={() => purge(j)}
              >
                {purging === j.id ? 'Deleting…' : 'Delete forever'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
